import type { FC, ReactNode } from 'react'
import type { Crumb } from '@/types'
import type { DiagramKey, SiteImage } from '@/types/content'
import type { BlueprintKind } from '@/components/site/blueprint'

import { ShieldCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import Blueprint from '@/components/site/blueprint'
import Breadcrumbs from '@/components/site/breadcrumbs'
import HeroTrust from '@/components/site/hero-trust'
import Photo from '@/components/site/photo'
import WindowDiagram from '@/components/sections/window-diagram'
import { riseDelay } from '@/lib/motion'

interface PageHeroProps {
    crumbs: Crumb[]
    title: string
    lead?: string
    image?: SiteImage | null
    diagram?: DiagramKey
    exteriorOnly?: boolean
    eyebrow?: string
    children?: ReactNode
    trust?: boolean
    aside?: ReactNode
    blueprint?: BlueprintKind | false
}

const DOOR_DIAGRAMS: DiagramKey[] = ['entry-door', 'patio-door', 'french-door', 'storm-door']

// Openings without a photo get a faint blueprint behind their right side: the
// matching drawing on window and door style pages, the house front elsewhere.
const pickBlueprint = (image: SiteImage | null | undefined, diagram: DiagramKey | undefined): BlueprintKind | null => {
    if (diagram) return DOOR_DIAGRAMS.includes(diagram) ? 'door' : 'window'

    return image ? null : 'house'
}

const PageHero: FC<PageHeroProps> = ({ crumbs, title, lead, image, diagram, exteriorOnly, eyebrow, children, trust = true, aside, blueprint }): ReactNode => {
    const hasMedia = Boolean(image || diagram || aside)
    const backdrop = blueprint === false ? null : (blueprint ?? pickBlueprint(image, diagram))

    return (
        <section className='relative overflow-hidden border-b border-border bg-gradient-to-b from-[#f1f6fb] to-background'>
            {backdrop && (
                <Blueprint
                    kind={backdrop}
                    className='absolute inset-y-0 right-0 hidden w-[62%] [mask-image:linear-gradient(to_right,transparent,#000_40%)] lg:block'
                />
            )}

            <div className='relative mx-auto grid max-w-7xl gap-7 px-4 pb-10 pt-5 sm:px-6 sm:pt-7 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:items-center lg:gap-12 lg:px-8 lg:pb-16 lg:pt-10'>
                <div className={hasMedia ? '' : 'lg:col-span-2 lg:max-w-[46rem]'}>
                    <Breadcrumbs items={crumbs} />

                    {eyebrow && (
                        <p className='hero-rise mt-6 text-sm font-bold uppercase tracking-[0.14em] text-primary'>
                            {eyebrow}
                        </p>
                    )}

                    <h1 className={cn('text-[2.375rem] font-bold leading-[1.05] sm:text-5xl lg:text-[3.5rem]', eyebrow ? 'mt-3' : 'mt-4 lg:mt-6')}>
                        <span className='line-mask'>
                            <span className='line-rise'>{title}</span>
                        </span>
                    </h1>

                    {lead && (
                        <p className='hero-rise mt-4 max-w-2xl text-base leading-relaxed sm:text-xl' style={riseDelay(120)}>
                            {lead}
                        </p>
                    )}

                    {exteriorOnly && (
                        <p className='hero-rise mt-5 inline-flex items-start gap-2 rounded-xl bg-[#fff4d6] px-4 py-3 text-base font-bold text-navy' style={riseDelay(180)}>
                            <ShieldCheck className='mt-0.5 size-5 shrink-0 text-[#a66b00]' aria-hidden='true' />
                            Exterior windows and doors only. We do not install interior doors or interior windows.
                        </p>
                    )}

                    {trust && (
                        <div className='hero-rise mt-6' style={riseDelay(240)}>
                            <HeroTrust />
                        </div>
                    )}

                    {children}
                </div>

                {hasMedia && (
                    <div className='hero-rise' style={riseDelay(150)}>
                        {diagram
                            ? (
                                <div className='mx-auto w-full max-w-[22rem] rounded-[2rem] border border-border bg-white p-6 shadow-sm sm:p-8 lg:max-w-none'>
                                    <WindowDiagram diagram={diagram} />
                                    <p className='mt-4 text-center text-base text-muted-foreground'>
                                        How it opens
                                    </p>
                                </div>
                            )
                            : image
                                ? (
                                    <Photo
                                        image={image}
                                        priority
                                        zoom
                                        className='aspect-[16/10] rounded-2xl shadow-lg sm:aspect-[3/2] lg:aspect-[6/5] lg:rounded-[2rem]'
                                        sizes='(min-width: 1024px) 40vw, 100vw'
                                    />
                                )
                                : aside}
                    </div>
                )}
            </div>
        </section>
    )
}

export default PageHero
